import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router";
import { meQueryKey, testModeSwitchKey } from "../../shared/session/queries";
import { useSession } from "../../shared/session/session-context";
import { useGuideState, useRecordGuide } from "./api";
import { GuidesContext, type Guides } from "./guides-context";
import {
  audienceOf,
  hasShowableStep,
  nextWhatsNew,
  onTourPage,
  snoozeKey,
  text,
  toursFor,
  welcomeToStart,
  type GuideRecordLike,
} from "./registry";
import { TourOverlay, type TourEnd } from "./TourOverlay";
import { useWhatsNew } from "../whatsNew/whatsNew-context";
import type { TourDef } from "./tours";

const LATER_KEY = "giraffe:guides:later";

/** "ไว้ทีหลัง" lasts for this browser session only; the tour is offered again on the next sign-in. */
function readLater(): ReadonlySet<string> {
  try {
    const raw = window.sessionStorage.getItem(LATER_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function writeLater(keys: ReadonlySet<string>): void {
  try {
    window.sessionStorage.setItem(LATER_KEY, JSON.stringify([...keys]));
  } catch {
    // Private mode or quota: "later" simply lasts until the page reloads.
  }
}

/** Welcome first, then the newest release first. */
function menuOrder(tours: readonly TourDef[]): TourDef[] {
  return [...tours].sort((a, b) => (a.kind === b.kind ? b.releasedOn.localeCompare(a.releasedOn) || a.id.localeCompare(b.id) : a.kind === "welcome" ? -1 : 1));
}

/** True once at least one step of the offered tour can show on this page (the page may still be loading). */
function useShowable(tour: TourDef | undefined): boolean {
  const [found, setFound] = useState<string | null>(null);
  useEffect(() => {
    if (!tour) return undefined;
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      if (hasShowableStep(tour)) {
        setFound(snoozeKey(tour));
        window.clearInterval(timer);
      } else if (tries >= 40) {
        window.clearInterval(timer);
      }
    }, 150);
    return () => window.clearInterval(timer);
  }, [tour]);
  return tour !== undefined && found === snoozeKey(tour);
}

function WhatsNewPrompt({ tour, onStart, onLater, onDismiss }: { tour: TourDef; onStart: () => void; onLater: () => void; onDismiss: () => void }) {
  const id = useId();
  return (
    // Answering the card is never an outside click on an open drawer or dialog (O-R10-2).
    // Thai by product decision (2026-09-30, re-affirmed 2026-10-05): the English-only chrome checks skip this surface.
    <aside className="gf-whatsnew" aria-labelledby={id} lang="th" data-dismiss-ignore="" data-chrome-exempt="whats-new">
      <h2 id={id}>มีอะไรใหม่</h2>
      <p className="gf-whatsnew-title">{text(tour.title)}</p>
      <p>{text(tour.summary)}</p>
      <div className="gf-whatsnew-actions">
        <button type="button" className="btn" onClick={onStart}>
          ดูแนะนำ
        </button>
        <button type="button" className="btn sec" onClick={onLater}>
          ไว้ทีหลัง
        </button>
        <button type="button" className="linkish" onClick={onDismiss}>
          ไม่ต้องแสดงอีก
        </button>
      </div>
    </aside>
  );
}

/**
 * Guided tours for the signed-in person (Features__GuidedTours). Starts the welcome tour by itself on a first sign-in,
 * then offers each "what's new" tour once with a small prompt (start / later / don't show again), only on the tour's own
 * page and only when something on that page can be shown. Seen and dismissed are stored per person on the server
 * (GET/PUT /me/guides), so they follow the person across browsers. In test mode nothing starts by itself and nothing is
 * recorded, for the admin or for the person being viewed; replay from the menu still works.
 */
export function GuidesProvider({ projectKey, children }: { projectKey: string; children: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const enabled = session.flag("GuidedTours");
  const inTestMode = session.testMode?.active === true;
  const state = useGuideState(enabled);
  // R8-D01: during an in-app test-mode switch the guide state is refetched for the new identity alongside /auth/me, and
  // either can land first. If the viewed person's state lands while the session still reads "not in test mode", the
  // welcome tour started by itself and its "seen" PUT was refused (403). So nothing starts or is recorded while a switch
  // is in flight, and nothing starts until the session and the guide state have both settled on the same identity.
  const switching = useIsMutating({ mutationKey: testModeSwitchKey }) > 0;
  const sessionFetching = useIsFetching({ queryKey: meQueryKey, exact: true }) > 0;
  const settled = !switching && !sessionFetching && !state.isFetching;
  const mayRecord = !inTestMode && !switching;
  const record = useRecordGuide();
  const [running, setRunning] = useState<TourDef | null>(null);
  // Tours ended or dismissed in this page session, so a failed save never restarts them in a loop.
  const [handled, setHandled] = useState<readonly GuideRecordLike[]>([]);
  const [later, setLater] = useState<ReadonlySet<string>>(readLater);

  const available = useMemo(() => (enabled ? menuOrder(toursFor(audienceOf(session))) : []), [enabled, session]);
  const records = useMemo(() => [...(state.data?.tours ?? []), ...handled], [state.data, handled]);
  // What's new (ruling of 17 Oct 2026): while the release dialog is due, open, or was shown in this page load, nothing starts by
  // itself here, so the two never stack; the welcome tour and the prompt wait for the next page load.
  const holdForRelease = useWhatsNew().holdTours;
  const automatic = enabled && state.isSuccess && settled && !inTestMode && !holdForRelease;
  const welcome = automatic && !running ? welcomeToStart(available, records) : undefined;
  const active = running ?? welcome ?? null;
  const offer = automatic && !active ? nextWhatsNew(available, records, location.pathname, later) : undefined;
  const showOffer = useShowable(offer);

  const remember = useCallback(
    (tour: TourDef, status: "seen" | "dismissed") => {
      setHandled((previous) => [...previous, { tourId: tour.id, version: tour.version, status }]);
      if (mayRecord) record.mutate({ tourId: tour.id, version: tour.version, status });
    },
    [mayRecord, record],
  );

  // The run whose "seen" is already recorded (at the first step shown), so its end does not record it twice.
  const recordedRun = useRef<string | null>(null);

  // O-R4-1: the tour is recorded as seen when it is first SHOWN, not only on Next/Skip/Esc, so closing the tab or
  // signing out mid-tour does not offer it again at the next sign-in. Pinning it as running keeps it on screen once
  // the record makes welcomeToStart() stop returning it.
  const onShown = useCallback(
    (tour: TourDef) => {
      setRunning(tour);
      recordedRun.current = snoozeKey(tour);
      remember(tour, "seen");
    },
    [remember],
  );

  const onEnd = useCallback(
    (tour: TourDef, reason: TourEnd) => {
      setRunning(null);
      const recorded = recordedRun.current === snoozeKey(tour);
      recordedRun.current = null;
      if (reason === "unavailable") {
        // Nothing of it is on this page: not seen. Only stop it restarting by itself in this page session.
        setLater((previous) => new Set([...previous, snoozeKey(tour)]));
        return;
      }
      if (!recorded) remember(tour, "seen");
    },
    [remember],
  );

  const guides = useMemo<Guides>(
    () => ({
      enabled,
      available,
      replay: (tourId) => {
        const tour = available.find((candidate) => candidate.id === tourId);
        if (!tour) return "คู่มือนี้ไม่มีให้ดูสำหรับบัญชีของคุณ";
        if (!onTourPage(tour, location.pathname)) {
          if (tour.openPath && projectKey) {
            void navigate(tour.openPath(projectKey));
          } else {
            return `คู่มือนี้อยู่ที่${tour.pageName ? text(tour.pageName) : "หน้าอื่น"} เปิดหน้านั้นก่อน แล้วกด Replay อีกครั้ง`;
          }
        }
        setRunning(tour);
        return null;
      },
    }),
    [enabled, available, location.pathname, projectKey, navigate],
  );

  return (
    <GuidesContext value={guides}>
      {children}
      {active && <TourOverlay key={active.id} tour={active} onShown={() => onShown(active)} onEnd={(reason) => onEnd(active, reason)} />}
      {offer && showOffer && (
        <WhatsNewPrompt
          tour={offer}
          onStart={() => setRunning(offer)}
          onLater={() => {
            const next = new Set([...later, snoozeKey(offer)]);
            setLater(next);
            writeLater(next);
          }}
          onDismiss={() => remember(offer, "dismissed")}
        />
      )}
    </GuidesContext>
  );
}
